import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { PersonaBriefDto } from "@/features/persona-opportunities/api/persona-opportunity-briefs.api";
import {
  buildPersonaBriefShareUrl,
  formatPersonaBriefForCopy,
} from "@/features/persona-opportunities/lib/persona-brief-clipboard";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";

import { PERSONA_SCRIPT_COPY_RESET_MS } from "./PersonaScriptEditableCell.js";

async function writeClipboard(text: string): Promise<boolean> {
  const copied = navigator.clipboard?.writeText(text);
  if (!copied) return false;
  try {
    await copied;
    return true;
  } catch {
    return false;
  }
}

/** Share-link and full-brief copy used by the workspace card footer and the hub drawer header. */
export function usePersonaBriefClipboard(
  brief: PersonaBriefDto,
  options?: { beforeShare?: () => Promise<boolean | void> },
) {
  const { t } = useTranslation();
  const [linkCopied, setLinkCopied] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const isSharingRef = useRef(false);
  const { copied, copy: copyText } = useCopyToClipboard({ resetMs: PERSONA_SCRIPT_COPY_RESET_MS });
  const beforeShareRef = useRef(options?.beforeShare);
  beforeShareRef.current = options?.beforeShare;

  const share = async () => {
    if (isSharingRef.current) return;
    isSharingRef.current = true;
    setIsSharing(true);
    try {
      const saved = await beforeShareRef.current?.();
      if (saved === false) {
        toast.error(
          t(
            "personaOpportunities.scripts.actions.shareSaveError",
            "Couldn't save the brief, so the share link wasn't opened.",
          ),
        );
        return;
      }
      const url = buildPersonaBriefShareUrl(brief.id, window.location.origin);
      window.open(url, "_blank", "noopener,noreferrer");
      if (!(await writeClipboard(url))) {
        toast.error(
          t(
            "personaOpportunities.scripts.actions.shareError",
            "Couldn't copy the share link. Please try again.",
          ),
        );
        return;
      }
      setLinkCopied(true);
      toast.success(
        t(
          "personaOpportunities.scripts.actions.shareToast",
          "Preview opened — link copied to clipboard",
        ),
      );
    } finally {
      isSharingRef.current = false;
      setIsSharing(false);
    }
  };

  const copy = async () => {
    if (!(await copyText(formatPersonaBriefForCopy(brief, t)))) {
      toast.error(
        t(
          "personaOpportunities.scripts.briefs.copyError",
          "Couldn't copy the brief. Please try again.",
        ),
      );
      return;
    }
    toast.success(
      t("personaOpportunities.scripts.briefs.copyToast", "Brief copied to clipboard"),
    );
  };

  return { linkCopied, isSharing, copied, share, copy };
}
